<template>
  <PageLayout>
    <template slot="header">
      <div class="d-flex m-3 mx-lg-0">
        <breadcrumb :list="breadcrumbList" />
        <div class="d-flex">
          <ExportData
            id="exportData"
            class="ml-2"
            :canExport="$can('use', 'comparationGroups.export')"
            @export="handleExportClick"
          />
        </div>
        <GroupsTreeMultiSelectFilter @loadReportsGroups="loadReportsGroups" />
      </div>
    </template>
    <template slot="main">
      <b-card v-if="$can('use', 'comparationGroups')" class="mb-3">
        <header slot="header">
          {{ labels.title }}
          <p class="subTitle">{{ labels.subTitle }}</p>
        </header>
        <div v-if="loadingReportsGroups" class="loading">
          <loading />
          <p>{{ labels.loadingReportsGroups }}</p>
        </div>
        <div v-if="!loadingReportsGroups" class="comparation-content">
          <div class="first-col" :class="{ expanded: metricsExpanded }">
            <div class="empty-div" :class="{ expanded: metricsExpanded }"></div>
            <div
              class="metric-desc-item"
              v-for="metric in availableRows"
              :key="metric.metricId"
            >
              <img
                v-if="!metric.isSubmetric"
                class="float-left"
                alt=""
                :class="{ expanded: metricsExpanded }"
                :src="
                  `https://static.teamculture.com.br/images/metrics/${metric.metricId}.svg`
                "
              />
              <transition name="fade">
                <span
                  class="metric-name"
                  :class="{ 'metric-has-submetrics': metric.hasSubmetrics }"
                  v-show="metricsExpanded"
                >
                  <div>
                    <em v-if="metric.isSubmetric" class="material-icons">
                      subdirectory_arrow_right
                    </em>
                    {{ metric.label }}
                  </div>
                </span>
              </transition>
            </div>
            <div class="metric-recall" @click="recall">
              <img
                v-show="metricsExpanded"
                alt=""
                class="float-left foward-icon"
                src="/icons/foward.svg"
              />
              <img
                v-show="!metricsExpanded"
                alt=""
                class="float-left toward-icon"
                src="/icons/toward.svg"
              />
              <transition name="fade">
                <span v-show="metricsExpanded">{{ labels.recall }}</span>
              </transition>
            </div>
          </div>
          <div
            class="second-col"
            @scroll="hideTooltip($event)"
            :class="{ expanded: metricsExpanded }"
          >
            <div class="groups-row">
              <div
                :class="{
                  'groups-item': true,
                  'text-truncate': true,
                  'inactive-group': group.inactive,
                  'd-none': !group.visible,
                }"
                v-for="group in internalGroupsData"
                :key="group.group_name"
                :id="group.group_name"
              >
                <span>{{ group.group_name }}</span>
                <b-tooltip :target="group.group_name">
                  <div class="tooltip-info">{{ group.group_name }}</div>
                </b-tooltip>
              </div>
            </div>
            <div
              class="group-metric-row"
              v-for="(metricsArr, index) in scoreMetrics"
              :key="`${index}-group-metric`"
            >
              <div
                v-for="(metric, idx) in metricsArr"
                :key="`${metric.metric_id}-item-${idx}-group-metric`"
                @click="accessRoute(metric, $event)"
                class="group-metric-item"
                :class="{
                  'd-none': !metric.visible,
                  'is-consolidated':
                    internalGroupsData[idx].group_id === 'consolidated',
                }"
                v-metric-color.update
                :data-metric-value="metric.score"
                :data-metric-type="metric.metric_id"
              >
                {{ metric.score }}
              </div>
            </div>
          </div>
        </div>
      </b-card>
      <modal-plans
        :visible="true"
        :hideHeaderClose="true"
        @hide="handleHide"
        v-if="!$can('use', 'comparationGroups')"
        ref="component-modal-plans"
        :title="labels.modalPlans.title"
      />
    </template>
  </PageLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import PageLayout from '@/components/Home/PageLayout.vue'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import ModalPlans from '@/components/Modal/ModalPlans.vue'
import scoreMetrics from '@/helpers/comparation'
import Loading from '@/components/Shared/Loading.vue'
import ExportData from '@/components/Reports/ExportData.vue'
import GroupsTreeMultiSelectFilter from '@/components/Comparation/GroupsTreeMultiSelectFilter.vue'
import { getUser } from '@/service/user'

export default {
  components: {
    PageLayout,
    Breadcrumb,
    ModalPlans,
    Loading,
    ExportData,
    GroupsTreeMultiSelectFilter,
  },
  beforeMount() {
    this.$store.dispatch('comparation/updateSelectedGroups', [])
    this.loadReportsGroups()
  },
  data() {
    return {
      metricsExpanded: true,
      disableFowardGroups: true,
      disableTowardGroups: true,
      loadingReportsGroups: false,
      internalGroupsData: [],
      availableRows: [],
      scoreMetrics: [],
    }
  },
  mounted() {
    this.$root.$emit('setPageTitle', { context: this, path: 'comparation' })
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
      groupsReports: 'comparation/groupsReports',
      selectedGroups: 'comparation/selectedGroups',
    }),
    labels() {
      return {
        ...this.resources.page.home.comparation,
        ...this.resources.general.menu,
        ...this.resources.general.buttons,
        ...this.resources.page.reports.engagement.smartMetrics.types,
      }
    },
    breadcrumbList() {
      return [
        {
          label: this.labels.report,
        },
        {
          label: this.labels.comparation,
        },
      ]
    },
  },
  methods: {
    async handleExportClick() {
      await this.$store.dispatch(
        'comparation/saveExportGroupsReports',
        this.selectedGroups
      )
      const env = window.env || process.env
      window.open(
        `${env.VUE_APP_EXPORTER_API_HOST}/comparison?token=${localStorage.jwt}&language=${localStorage.languageabrev}`,
        '_blank'
      )
    },

    setScoreMetrics() {
      this.scoreMetrics = scoreMetrics(
        this.availableRows,
        this.internalGroupsData
      )
    },

    async loadReportsGroups(groups = []) {
      try {
        this.loadingReportsGroups = true
        await this.$store.dispatch('comparation/loadReportsGroupsFiltered', {
          groups,
        })
        this.setAvailableRows(this.groupsReports)
        this.internalGroupsData = this.groupsReports
        this.setScoreMetrics()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('TODO ', error)
      } finally {
        this.loadingReportsGroups = false
      }
    },

    setAvailableRows(groupsData) {
      const availableRows = []
      const firstGroupsData = groupsData[0]
      firstGroupsData.metrics.forEach(
        ({ metric_id: metricId, label, submetrics = [] }) => {
          availableRows.push({
            metricId,
            label: this.formatLabel(metricId, label),
            isSubmetric: false,
            hasSubmetrics: submetrics.length > 0,
          })
          if (submetrics.length) {
            submetrics.forEach(({ submetric_id: submetricId, label }) => {
              availableRows.push({
                metricId: `${metricId}:${submetricId}`,
                label,
                isSubmetric: true,
                hasSubmetrics: false,
              })
            })
          }
        }
      )
      this.availableRows = availableRows
    },

    formatLabel(metricId, label) {
      switch (metricId) {
        case 'averageScore':
          return this.labels.averageScore
        case 'enpscore':
          return this.labels.enpscore
        case 'lnpscore':
          return this.labels.lnpscore
        case 'participation':
          return this.labels.participation
        case 'culturalAlignment':
          return this.labels.culturalAlignment
        case 'culturalEntropy':
          return this.labels.culturalEntropy
        case 'diversity':
          return this.labels.diversity
        case 'psychologicalSafety':
          return this.labels.psychologicalSafety
        case 'burnout':
          return this.labels.burnout
        case 'maturityLevel':
          return this.labels.maturityLevel
        case 'psychosocialRisk':
          return this.labels.psychosocialRisk
        default:
          return label
      }
    },

    recall() {
      this.metricsExpanded = !this.metricsExpanded
    },

    hideTooltip() {
      this.$root.$emit('bv::hide::tooltip')
    },

    handleHide: function($event) {
      this.$router.push({ name: 'dashboard' })
      $event.preventDefault()
    },

    accessRoute({ groupId, metric_id: metricId, score }, $event) {
      if (groupId === 'consolidated') {
        return $event.preventDefault()
      }

      const { companyId } = getUser()
      if (companyId === groupId) groupId = 'company'
      const selectedGroup = groupId === 'company' ? null : groupId
      this.$store.dispatch('user/updateSelectedGroup', selectedGroup)
      return score === 'N/A'
        ? $event.preventDefault()
        : this.setAccessRoute(groupId, metricId)
    },

    setAccessRoute(groupId, metricId) {
      switch (metricId) {
        case 'averageScore':
        case 'enpscore':
        case 'lnpscore':
        case 'diversity':
        case 'psychologicalSafety':
        case 'burnout':
        case 'psychosocialRisk':
          return this.$router.push(`/reports/${groupId}`)
        case 'participation':
          return this.$router.push(`/participation/${groupId}`)
        case 'culturalAlignment':
        case 'culturalEntropy':
        case 'maturityLevel':
          return this.$router.push(`/culture/${groupId}`)
        default:
          return this.$router.push(
            `/reports/${groupId}/metric/${metricId.split(':')[0]}`
          )
      }
    },

    toggleGroupVisibility({ groups }) {
      this.internalGroupsData = groups
      this.setScoreMetrics()
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

$metricHeight: 45px;
$metricDescWidthExpanded: 151px;
$metricDescWidthMinified: 45px;
$metricMarginBottom: 4px;
$groupsRowHeight: 96.5px;
$secondColMaxWidthLg: 700px;
$secondColMaxWidthXlg: 900px;

header {
  .d-flex {
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }
}

.fade-leave-active {
  transition: opacity 0ms;
}

/deep/ .subTitle {
  color: map-get($theme-colors, 'gray02');
  font-size: 12px;
  margin: 0;
  text-decoration: none;
}

.card-body {
  padding-top: 0;
}

.icon-disabled {
  opacity: 0.3;
}

.comparation-content {
  display: flex;
  height: 100%;
  float: left;
  max-width: 100%;
  white-space: nowrap;
}

.first-col {
  position: relative;

  &.expanded {
    width: $metricDescWidthExpanded;
    .metric-desc-item,
    .metric-recall,
    .empty-div {
      width: $metricDescWidthExpanded;
    }
  }

  .empty-div {
    background-color: map-get($theme-colors, 'white');
    height: $groupsRowHeight;
    width: $metricDescWidthMinified;
  }

  .metric-recall {
    cursor: pointer;
  }

  .metric-desc-item,
  .metric-recall {
    background-color: map-get($theme-colors, 'quinary');
    border-radius: 4px;
    color: map-get($theme-colors, 'white');
    font-size: 10px;
    height: $metricHeight;
    line-height: 13px;
    padding: 15px 15px 0 4px;
    text-align: left;
    transition: width 200ms ease;
    width: $metricDescWidthMinified;
    white-space: normal;

    &:not(:last-of-type) {
      margin-bottom: $metricMarginBottom;
    }

    &.metric-recall {
      background-color: map-get($theme-colors, 'primary');
    }

    img {
      height: 35px;
      margin-top: -10px;
      width: 35px;

      &.foward-icon,
      &.toward-icon {
        margin-top: -2px;
        height: 15px;
      }

      &.expanded {
        margin-right: 5px;
      }
    }
  }
}

.second-col {
  flex-basis: 100%;
  flex-grow: 1;
  vertical-align: top;
  overflow-x: auto;
  padding-left: 5px;
  width: 100%;
  white-space: nowrap;

  .groups-row {
    height: $groupsRowHeight;
  }

  .groups-item {
    $border: 1px solid map-get($theme-colors, 'purple');
    border-left: $border;
    color: map-get($theme-colors, 'purple05');
    display: inline-block;
    font-size: 14px;
    height: 100%;
    line-height: 14px;
    margin: 0 1px;
    padding: 10px 15px;
    text-align: left;
    transform: rotate(-180deg);
    width: $metricHeight;
    writing-mode: tb-rl;

    &:first-of-type {
      border-right: $border;
      margin-left: 0;
    }

    &:last-of-type {
      margin: 0;
    }
  }

  .group-metric-item {
    border-radius: 4px;
    cursor: pointer;
    display: inline-block;
    height: $metricHeight;
    font-weight: 500;
    text-align: center;
    margin: 0 2px $metricMarginBottom;
    padding-top: 10px;
    width: $metricHeight - 2px;

    &.metric-blank {
      background-color: map-get($theme-colors, 'metric-blank');
      cursor: not-allowed;
    }

    &.metric-low {
      background-color: map-get($theme-colors, 'metric-low');

      &:hover {
        background-color: lighten(map-get($theme-colors, 'metric-low'), 10%);
      }

      &:active {
        background-color: darken(map-get($theme-colors, 'metric-low'), 10%);
      }
    }

    &.metric-medium {
      background-color: map-get($theme-colors, 'metric-medium');

      &:hover {
        background-color: lighten(map-get($theme-colors, 'metric-medium'), 10%);
      }

      &:active {
        background-color: darken(map-get($theme-colors, 'metric-medium'), 10%);
      }
    }

    &.metric-high {
      background-color: map-get($theme-colors, 'metric-high');

      &:hover {
        background-color: lighten(map-get($theme-colors, 'metric-high'), 10%);
      }

      &:active {
        background-color: darken(map-get($theme-colors, 'metric-high'), 10%);
      }
    }

    &:first-of-type {
      margin-left: 0;
    }
  }
}

.loading {
  margin: 0 auto;
  text-align: center;
}

.inactive-group {
  background: map-get($theme-colors, 'gray08') !important;
}

.is-consolidated {
  cursor: not-allowed !important;
}
</style>
